import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './Expenses.css';
import ManagerSidebar from "../../../components/Sidebar/ManagerSidebar";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faTrash, faSave, faTimes, faPlus,
    faArrowLeft, faArrowRight, faMoneyBillWave,
    faTag, faAlignLeft, faSearch, faChartLine, faFilePdf,
    faEdit, faCheck
} from '@fortawesome/free-solid-svg-icons';

const Expenses = () => {
    const [expenses, setExpenses] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [showAddForm, setShowAddForm] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);

    // Dynamic Option Arrays collected from the Configuration Endpoint
    const [existingDescriptions, setExistingDescriptions] = useState([]);
    const [existingCategories, setExistingCategories] = useState([]);

    // Custom toggle switches to reveal fallback manual input fields if needed
    const [customDescription, setCustomDescription] = useState(false);
    const [customCategory, setCustomCategory] = useState(false);

    // Form state for creating new expense
    const [newExpense, setNewExpense] = useState({
        description: '',
        amount: '',
        date: '',
        category: ''
    });

    // Inline editing states
    const [editingId, setEditingId] = useState(null);
    const [editFile, setEditFile] = useState(null);
    const [editForm, setEditForm] = useState({
        description: '',
        amount: '',
        date: '',
        category: ''
    });

    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8;

    // Fetch both datasets when the page mounts
    useEffect(() => { 
        fetchExpenses(); 
        fetchDropdownConfigurations();
    }, []);

    const fetchExpenses = async () => {
        try {
            const res = await axios.get(
                'https://ikaze-thouse-backend.vercel.app/api/v1/expenses/getAll'
            );
            if (res.data.success) {
                setExpenses(res.data.data.sort((a, b) => b.expenseid - a.expenseid));
            }
        } catch {
            console.error('Error fetching expenses');
        }
    };

    // NEW: Fetch matching configuration list elements directly from your base endpoint
    const fetchDropdownConfigurations = async () => {
        try {
            const res = await axios.get(
                'https://ikaze-thouse-backend.vercel.app/api/expenses-details'
            );
            const dataArray = res.data.data || [];
            
            // Extract, clean, and deduplicate values matching database property schemes
            const uniqueDescriptions = [...new Set(dataArray.map(item => item.description || item.Description).filter(Boolean))];
            const uniqueCategories = [...new Set(dataArray.map(item => item.category || item.Category).filter(Boolean))];
            
            setExistingDescriptions(uniqueDescriptions);
            setExistingCategories(uniqueCategories);
        } catch (error) {
            console.error('Error fetching configuration options:', error);
        }
    };

    const handleAddInputChange = (e) => {
        const { name, value } = e.target;
        setNewExpense(prev => ({ ...prev, [name]: value }));
    };

    const handleSelectDescriptionChange = (e) => {
        const val = e.target.value;
        if (val === "CUSTOM_NEW_VAL") {
            setCustomDescription(true);
            setNewExpense(prev => ({ ...prev, description: "" }));
        } else {
            setCustomDescription(false);
            setNewExpense(prev => ({ ...prev, description: val }));
        }
    };

    const handleSelectCategoryChange = (e) => {
        const val = e.target.value;
        if (val === "CUSTOM_NEW_VAL") {
            setCustomCategory(true);
            setNewExpense(prev => ({ ...prev, category: "" }));
        } else {
            setCustomCategory(false);
            setNewExpense(prev => ({ ...prev, category: val }));
        }
    };

    const handleFileChange = (e) => {
        setSelectedFile(e.target.files[0]);
    };

    const handleAddExpense = async () => {
        if (!newExpense.description || !newExpense.amount || !newExpense.date || !newExpense.category) {
            return alert('Please fill in all required fields');
        }

        const formData = new FormData();
        formData.append('description', newExpense.description);
        formData.append('amount', parseFloat(newExpense.amount));
        formData.append('date', newExpense.date);
        formData.append('category', newExpense.category);

        if (selectedFile) {
            formData.append('invoice', selectedFile);
        }

        try {
            await axios.post(
                'https://ikaze-thouse-backend.vercel.app/api/v1/expenses/create',
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );

            setShowAddForm(false);
            setNewExpense({ description: '', amount: '', date: '', category: '' });
            setSelectedFile(null);
            setCustomDescription(false);
            setCustomCategory(false);
            fetchExpenses();
            // Refresh configurations list in case they added custom targets
            fetchDropdownConfigurations();
        } catch {
            alert("Failed to add expense");
        }
    };

    const startEditing = (expense) => {
        setEditingId(expense.expenseid);
        setEditFile(null);
        setEditForm({
            description: expense.description,
            amount: expense.amount,
            date: expense.date ? expense.date.substring(0, 10) : '',
            category: expense.category || ''
        });
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleUpdateExpense = async (id) => {
        if (!editForm.description || !editForm.amount || !editForm.date || !editForm.category) {
            return alert('Please fill in all required fields');
        }

        const formData = new FormData();
        formData.append('description', editForm.description);
        formData.append('amount', parseFloat(editForm.amount));
        formData.append('date', editForm.date);
        formData.append('category', editForm.category);

        if (editFile) {
            formData.append('invoice', editFile);
        }

        try {
            await axios.put(
                `https://ikaze-thouse-backend.vercel.app/api/v1/expenses/update/${id}`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setEditingId(null);
            setEditFile(null);
            fetchExpenses();
            fetchDropdownConfigurations();
        } catch {
            alert("Failed to update expense");
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Delete this record?')) {
            await axios.delete(
                `https://ikaze-thouse-backend.vercel.app/api/v1/expenses/delete/${id}`
            );
            fetchExpenses();
        }
    };

    const filteredExpenses = expenses.filter(e =>
        e.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        e.category.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const totalAmount = filteredExpenses.reduce(
        (sum, e) => sum + (parseFloat(e.amount) || 0), 0
    );

    const totalPages = Math.ceil(filteredExpenses.length / itemsPerPage);

    const currentExpenses = filteredExpenses.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className="expenses-container">
            <ManagerSidebar />

            <main className="expenses-main">
                <header className="expenses-header">
                    <div className="header-info">
                        <h1>Expense Analytics</h1>
                        <p>Track and manage company expenditures (RWF)</p>
                    </div>

                    <button
                        className={`btn-primary ${showAddForm ? 'cancel' : ''}`}
                        onClick={() => {
                            setShowAddForm(!showAddForm);
                            setCustomDescription(false);
                            setCustomCategory(false);
                        }}
                    >
                        <FontAwesomeIcon icon={showAddForm ? faTimes : faPlus} />
                        {showAddForm ? ' Close Form' : ' New Expense'}
                    </button>
                </header>

                <section className="stats-overview">
                    <div className="stat-card">
                        <div className="icon-box blue">
                            <FontAwesomeIcon icon={faChartLine} />
                        </div>
                        <div className="stat-text">
                            <label>Total Expenses</label>
                            <h3>{totalAmount.toLocaleString()} RWF</h3>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="icon-box green">
                            <FontAwesomeIcon icon={faTag} />
                        </div>
                        <div className="stat-text">
                            <label>Active Categories</label>
                            <h3>{new Set(expenses.map(e => e.category)).size}</h3>
                        </div>
                    </div>

                    <div className="stat-card">
                        <div className="icon-box orange">
                            <FontAwesomeIcon icon={faMoneyBillWave} />
                        </div>
                        <div className="stat-text">
                            <label>Latest Transaction</label>
                            <h3>
                                {expenses[0]?.amount
                                    ? `${parseFloat(expenses[0].amount).toLocaleString()} RWF`
                                    : "0 RWF"}
                            </h3>
                        </div>
                    </div>
                </section>

                {showAddForm && (
                    <div className="glass-form-container">
                        <div className="form-header">
                            <h3>Transaction Details</h3>
                            <p>Select predefined values or enter new custom variants below</p>
                        </div>

                        <div className="form-grid">
                            {/* Description Controller */}
                            <div className="input-wrapper">
                                <label>Description</label>
                                <div className="input-field">
                                    <FontAwesomeIcon icon={faAlignLeft} />
                                    {!customDescription ? (
                                        <select
                                            className="form-select-dropdown"
                                            onChange={handleSelectDescriptionChange}
                                            defaultValue=""
                                        >
                                            <option value="" disabled>-- Choose Description --</option>
                                            {existingDescriptions.map((desc, idx) => (
                                                <option key={idx} value={desc}>{desc}</option>
                                            ))}
                                            <option value="CUSTOM_NEW_VAL" className="add-custom-option">+ Add Custom Description...</option>
                                        </select>
                                    ) : (
                                        <div className="custom-input-with-reset">
                                            <input
                                                name="description"
                                                onChange={handleAddInputChange}
                                                value={newExpense.description}
                                                placeholder="Enter new custom description"
                                                autoFocus
                                            />
                                            <button type="button" className="reset-inline-btn" onClick={() => setCustomDescription(false)} title="Back to Dropdown">✕</button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Amount Input */}
                            <div className="input-wrapper">
                                <label>Amount (RWF)</label>
                                <div className="input-field">
                                    <FontAwesomeIcon icon={faMoneyBillWave} />
                                    <input
                                        name="amount"
                                        type="number"
                                        onChange={handleAddInputChange}
                                        value={newExpense.amount}
                                        placeholder="0"
                                    />
                                </div>
                            </div>

                            {/* Date Input */}
                            <div className="input-wrapper">
                                <label>Date</label>
                                <div className="input-field">
                                    <FontAwesomeIcon icon={faAlignLeft} />
                                    <input
                                        type="date"
                                        name="date"
                                        onChange={handleAddInputChange}
                                        value={newExpense.date}
                                    />
                                </div>
                            </div>

                            {/* Category Controller */}
                            <div className="input-wrapper">
                                <label>Category</label>
                                <div className="input-field">
                                    <FontAwesomeIcon icon={faTag} />
                                    {!customCategory ? (
                                        <select
                                            className="form-select-dropdown"
                                            onChange={handleSelectCategoryChange}
                                            defaultValue=""
                                        >
                                            <option value="" disabled>-- Choose Category --</option>
                                            {existingCategories.map((cat, idx) => (
                                                <option key={idx} value={cat}>{cat}</option>
                                            ))}
                                            <option value="CUSTOM_NEW_VAL" className="add-custom-option">+ Add Custom Category...</option>
                                        </select>
                                    ) : (
                                        <div className="custom-input-with-reset">
                                            <input
                                                name="category"
                                                onChange={handleAddInputChange}
                                                value={newExpense.category}
                                                placeholder="Enter new custom category"
                                                autoFocus
                                            />
                                            <button type="button" className="reset-inline-btn" onClick={() => setCustomCategory(false)} title="Back to Dropdown">✕</button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Invoice File Input */}
                            <div className="input-wrapper">
                                <label>Invoice (PDF)</label>
                                <div className="input-field">
                                    <FontAwesomeIcon icon={faFilePdf} />
                                    <input
                                        type="file"
                                        accept=".pdf"
                                        onChange={handleFileChange}
                                    />
                                </div>
                            </div>
                        </div>

                        <button className="btn-submit" onClick={handleAddExpense}>
                            <FontAwesomeIcon icon={faSave} /> Save Expense
                        </button>
                    </div>
                )}

                <div className="table-section">
                    <div className="table-toolbar">
                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} />
                            <input
                                type="text"
                                placeholder="Search expenses..."
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="responsive-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Date</th>
                                    <th>Description</th>
                                    <th>Category</th>
                                    <th>Amount (RWF)</th>
                                    <th>Invoice</th>
                                    <th>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {currentExpenses.map(e => {
                                    const isEditing = editingId === e.expenseid;
                                    return (
                                        <tr key={e.expenseid} className={isEditing ? 'row-editing' : ''}>
                                            <td>#{e.expenseid}</td>

                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="date"
                                                        name="date"
                                                        className="table-input"
                                                        value={editForm.date}
                                                        onChange={handleEditInputChange}
                                                    />
                                                ) : (
                                                    e.date?.substring(0, 10)
                                                )}
                                            </td>

                                            <td>
                                                {isEditing ? (
                                                    <select
                                                        name="description"
                                                        className="table-input"
                                                        value={editForm.description}
                                                        onChange={handleEditInputChange}
                                                    >
                                                        {existingDescriptions.map((desc, idx) => (
                                                            <option key={idx} value={desc}>{desc}</option>
                                                        ))}
                                                        {!existingDescriptions.includes(editForm.description) && (
                                                            <option value={editForm.description}>{editForm.description}</option>
                                                        )}
                                                    </select>
                                                ) : (
                                                    e.description
                                                )}
                                            </td>

                                            <td>
                                                {isEditing ? (
                                                    <select
                                                        name="category"
                                                        className="table-input"
                                                        value={editForm.category}
                                                        onChange={handleEditInputChange}
                                                    >
                                                        {existingCategories.map((cat, idx) => (
                                                            <option key={idx} value={cat}>{cat}</option>
                                                        ))}
                                                        {!existingCategories.includes(editForm.category) && (
                                                            <option value={editForm.category}>{editForm.category}</option>
                                                        )}
                                                    </select>
                                                ) : (
                                                    e.category
                                                )}
                                            </td>

                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="number"
                                                        name="amount"
                                                        className="table-input"
                                                        value={editForm.amount}
                                                        onChange={handleEditInputChange}
                                                    />
                                                ) : (
                                                    `${parseFloat(e.amount).toLocaleString()} RWF`
                                                )}
                                            </td>
                                            <td>
                                                {isEditing ? (
                                                    <input
                                                        type="file"
                                                        accept=".pdf"
                                                        className="table-input-file"
                                                        onChange={(e) => setEditFile(e.target.files[0])}
                                                    />
                                                ) : e.invoice ? (
                                                    <a href={e.invoice} target="_blank" rel="noreferrer" className="pdf-link">
                                                        View PDF
                                                    </a>
                                                ) : (
                                                    "No file"
                                                )}
                                            </td>

                                            <td>
                                                <div className="action-buttons">
                                                    {isEditing ? (
                                                        <>
                                                            <button className="btn-table-action save" onClick={() => handleUpdateExpense(e.expenseid)} title="Save Changes">
                                                                <FontAwesomeIcon icon={faCheck} />
                                                            </button>
                                                            <button className="btn-table-action cancel" onClick={() => { setEditingId(null); setEditFile(null); }} title="Cancel">
                                                                <FontAwesomeIcon icon={faTimes} />
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button className="btn-table-action edit" onClick={() => startEditing(e)} title="Edit Row">
                                                                <FontAwesomeIcon icon={faEdit} />
                                                            </button>
                                                            <button className="btn-table-action delete" onClick={() => handleDelete(e.expenseid)} title="Delete Row">
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="pagination-footer">
                        <p>Page {currentPage} of {totalPages || 1}</p>
                        <div>
                            <button
                                disabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}
                            >
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </button>

                            <button
                                disabled={currentPage >= totalPages}
                                onClick={() => setCurrentPage(p => p + 1)}
                            >
                                <FontAwesomeIcon icon={faArrowRight} />
                            </button>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Expenses;